import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Send, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("El mensaje debe tener al menos 10 caracteres");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/contact", form);
      toast.success(r.data?.message || "Mensaje enviado");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar tu mensaje");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App" data-testid="contacto-page">
      <Navbar />
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="overline text-amber-700">Estamos aquí</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
            Hablemos
          </h1>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
            ¿Tienes dudas sobre publicar tu negocio, una oportunidad o cómo funciona la intermediación?
            Escríbenos y te responderemos a la brevedad.
          </p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="space-y-6">

          {/* Form */}
          <div>
            {sent ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 sm:p-8 text-center" data-testid="contacto-success">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h2 className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight">¡Mensaje enviado!</h2>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                  Recibimos tu consulta. Nuestro equipo se pondrá en contacto contigo en las próximas horas hábiles.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                  <Button onClick={() => setSent(false)} variant="outline" data-testid="contacto-send-another">
                    Enviar otro mensaje
                  </Button>
                  <Button asChild className="btn-primary">
                    <Link to="/marketplace">Ver negocios disponibles</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl p-5 sm:p-7 space-y-4" data-testid="contacto-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nombre completo *</Label>
                    <Input id="name" required minLength={2} value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="contacto-name" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="contacto-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Teléfono (opcional)</Label>
                    <Input id="phone" inputMode="tel" placeholder="+56 9 ..." value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="contacto-phone" />
                  </div>
                  <div>
                    <Label htmlFor="subject">Asunto *</Label>
                    <Input id="subject" required minLength={2} placeholder="Tema de tu consulta" value={form.subject} onChange={(e) => upd("subject", e.target.value)} data-testid="contacto-subject" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">Mensaje *</Label>
                  <Textarea
                    id="message" rows={6} required minLength={10} maxLength={4000}
                    placeholder="Cuéntanos en qué te podemos ayudar..."
                    value={form.message}
                    onChange={(e) => upd("message", e.target.value)}
                    data-testid="contacto-message"
                  />
                  <p className="mt-1 text-xs text-slate-400 text-right">{form.message.length}/4000</p>
                </div>
                <Button type="submit" disabled={loading} className="w-full sm:w-auto btn-primary h-11" data-testid="contacto-submit">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Enviando..." : "Enviar mensaje"}
                </Button>
              </form>
            )}
          </div>

          {/* Data-protection notice (Ley 19.628) */}
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 sm:p-6 flex items-start gap-4"
            data-testid="contacto-data-protection"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="text-sm text-emerald-900 leading-relaxed">
              <p className="font-semibold">Tus datos están protegidos</p>
              <p className="mt-1">
                Los datos que entregas en este formulario quedan resguardados bajo la{" "}
                <strong>Ley N° 19.628 sobre Protección de la Vida Privada</strong> de la
                República de Chile. Solo se utilizan para responder tu consulta y nunca se
                comparten con terceros. Conoce más en nuestra{" "}
                <Link to="/privacidad" className="underline font-semibold">política de privacidad</Link>.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
